import {useAccessToken} from "../../services/auth"
import React, {useState} from "react"
import {
	getOutreachSettingsList,
	isEmailOutreachSettings,
	isMessageOutreachSettings,
	makeOutreachSettingsDefault,
} from "../../resources/outreach"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {
	DataTable,
	DataTableActionHeader,
	DataTableBody,
	DataTableCard,
	DataTableCell,
	DataTableHead,
	DataTableRow,
	TablePending,
} from "../../components/DataTable/DataTable"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import Icon from "../../components/Icon/Icon"
import {toPhoneNumber} from "../../resources/common"
import Button from "../../components/Button/Button"
import {useModal} from "react-modal-hook"
import {CreateOutreachSettingsModal, EditOutreachSettingsModal} from "./OutreachSettingsModals"
import {DataTableActionButton, DataTableActionItem} from "../../components/DataTable/DataTableActionButton"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import Tag from "../../components/Tag/Tag"

export default function OutreachSettings({refresh}: {refresh: () => void}) {
	const accessToken = useAccessToken()
	const result = useAsyncResult(() => getOutreachSettingsList(accessToken))
	const [, setMakeDefault] = useAsyncResultIdle((id: string) => makeOutreachSettingsDefault(accessToken, id))
	const emailOutreachSettingsResult = result.map((v) =>
		v.filter(isEmailOutreachSettings).sort((a, b) => b.id.localeCompare(a.id))
	)
	const messageOutreachSettingsResult = result.map((v) => v.filter(isMessageOutreachSettings))
	const [selectedSettingsId, setSelectedSettingsId] = useState<string>()
	const [showCreateModal, hideCreateModal] = useModal(() => (
		<CreateOutreachSettingsModal close={hideCreateModal} accessToken={accessToken} onSuccess={refresh} />
	))
	const [showEditModal, hideEditModal] = useModal(
		() => (
			<EditOutreachSettingsModal
				close={hideEditModal}
				accessToken={accessToken}
				onSuccess={refresh}
				id={selectedSettingsId as string}
			/>
		),
		[selectedSettingsId]
	)

	return (
		<div className={"outreach-settings columns"}>
			<DataTableCard className={"email-outreach-settings-card column is-6"}>
				<DataTableActionHeader
					title={
						<div className={"data-table-title-container"}>
							<div className={"data-table-text-title"}>
								<Icon icon="email-envelope-1--emails" size={20} /> <span>Email Addresses</span>
							</div>
							<Button theme={"neutral"} prefixIcon={"add-plus-small"} onClick={showCreateModal}>
								New
							</Button>
						</div>
					}
				></DataTableActionHeader>
				<AsyncResultComponent asyncResult={emailOutreachSettingsResult} pendingComponent={<TablePending />}>
					{({value: emailOutreachSettings}) => {
						return (
							<DataTable>
								<DataTableHead>
									<DataTableRow>
										<th>ID</th>
										<th>Name</th>
										<th>Sender</th>
										<th>Is Verified</th>
										<th></th>
									</DataTableRow>
								</DataTableHead>
								<DataTableBody>
									{emailOutreachSettings.map((settings) => (
										<DataTableRow
											key={settings.id}
											onClick={() => {
												setSelectedSettingsId(settings.id)
												showEditModal()
											}}
										>
											<DataTableCell>{settings.id}</DataTableCell>
											<DataTableCell>
												{settings.attributes.name}{" "}
												{settings.attributes.isDefault && (
													<Tag size={"small"} content={"Default"} variant={"outline"} theme={"dark"} />
												)}
											</DataTableCell>
											<DataTableCell>{settings.attributes.sender.email}</DataTableCell>
											<DataTableCell>{settings.attributes.isVerified ? "Yes" : "No"}</DataTableCell>
											<DataTableActionButton
												enableActions={!settings.attributes.isDefault && settings.attributes.isVerified}
											>
												<DataTableActionItem
													onClick={async () => {
														await setMakeDefault(settings.id)
														refresh()
													}}
													title={"Make Default"}
													icon={"target-goal"}
												/>
											</DataTableActionButton>
										</DataTableRow>
									))}
								</DataTableBody>
							</DataTable>
						)
					}}
				</AsyncResultComponent>
			</DataTableCard>

			<DataTableCard className={"message-outreach-settings-card column is-6"}>
				<DataTableActionHeader
					title={
						<div className={"data-table-title-container"}>
							<div className={"data-table-text-title"}>
								<Icon icon="phone-forward-arrow" size={20} /> <span>Phone Numbers</span>
							</div>
							<div>
								<Button theme={"neutral"} prefixIcon={"add-plus-small"} disabled>
									New
								</Button>
							</div>
						</div>
					}
				></DataTableActionHeader>
				<AsyncResultComponent asyncResult={messageOutreachSettingsResult} pendingComponent={<TablePending />}>
					{({value: messageOutreachSettings}) => {
						return (
							<DataTable>
								<DataTableHead>
									<DataTableRow>
										<th>ID</th>
										<th>Name</th>
										<th>Sender</th>
									</DataTableRow>
								</DataTableHead>
								<DataTableBody>
									{messageOutreachSettings.map((settings) => (
										<DataTableRow key={settings.id}>
											<td>{settings.id}</td>
											<td>{settings.attributes.name}</td>
											<td>{toPhoneNumber(settings.attributes.senderPhone)}</td>
										</DataTableRow>
									))}
								</DataTableBody>
							</DataTable>
						)
					}}
				</AsyncResultComponent>
			</DataTableCard>
		</div>
	)
}
